<template>
  <div class="p-p-6">
    <div class="p-mb-6">
      <h1 class="p-text-primary p-mb-2 p-text-4xl p-font-bold"><i class="pi pi-cog"></i> Адміністрація</h1>
      <p class="p-text-secondary p-m-0 p-text-lg">Управління тестами, сесіями та користувачами</p>
    </div>

    <div class="p-bg-surface-card p-rounded-lg p-shadow p-p-6">
      <TabView>
        <TabPanel header="Тестові сесії">
                      <div class="p-mb-6">
              <div class="p-flex p-justify-between p-ai-center p-mb-6">
                <h2 class="p-m-0 p-text-2xl p-font-semibold p-text-color">Всі сесії</h2>
              <Button
                label="Створити сесію"
                icon="pi pi-plus"
                class="p-button-success"
                @click="showCreateSessionDialog = true"
              />
            </div>

            <DataTable :value="activeSessions" :paginator="true" :rows="10" class="p-mb-6">
              <Column field="name" header="Назва">
                <template #body="slotProps">
                  <div class="p-flex p-ai-center p-gap-2 p-flex-wrap">
                    <span>{{ slotProps.data.name }}</span>
                    <Tag 
                      v-if="slotProps.data.is_anonymous" 
                      value="Анонімні відповіді" 
                      severity="info" 
                      class="p-text-xs p-px-2 p-py-1"
                    />
                  </div>
                </template>
              </Column>
              <Column field="test_type_name" header="Тип тесту">
                <template #body="slotProps">
                  {{ slotProps.data.test_type_name || (slotProps.data.test_type ? slotProps.data.test_type.name : 'Невідомо') }}
                </template>
              </Column>
              <Column field="start_date" header="Початок">
                <template #body="slotProps">
                  {{ formatDate(slotProps.data.start_date) }}
                </template>
              </Column>
              <Column field="end_date" header="Кінець">
                <template #body="slotProps">
                  {{ formatDate(slotProps.data.end_date) }}
                </template>
              </Column>
              <Column field="participants.length" header="Учасників"></Column>
              <Column header="Анонімні відповіді" v-if="hasAnonymousSessions">
                <template #body="slotProps">
                  <div v-if="slotProps.data.is_anonymous" class="p-flex p-flex-column p-gap-1">
                    <div class="p-flex p-ai-center p-gap-1 p-text-xs">
                      <i class="pi pi-users p-text-primary"></i>
                      <span>{{ slotProps.data.anonymous_responses_count || 0 }}</span>
                    </div>
                    <div class="p-flex p-ai-center p-gap-1 p-text-xs">
                      <i class="pi pi-chart-line p-text-primary"></i>
                      <span>{{ getAnonymousCompletionRate(slotProps.data) }}%</span>
                    </div>
                  </div>
                  <span v-else class="p-text-secondary p-text-xs">-</span>
                </template>
              </Column>
              <Column field="status" header="Статус">
                <template #body="slotProps">
                  <Tag :value="getStatusLabel(slotProps.data.status)" :severity="getStatusSeverity(slotProps.data.status)" />
                </template>
              </Column>
              <Column header="Анонімне посилання" v-if="hasAnonymousSessions">
                <template #body="slotProps">
                  <div v-if="slotProps.data.is_anonymous && slotProps.data.anonymous_access_url" class="p-flex p-ai-center p-gap-2 p-flex-wrap">
                    <div class="p-max-w-200 p-overflow-hidden p-text-ellipsis p-whitespace-nowrap">
                      <a :href="slotProps.data.anonymous_access_url" target="_blank" class="p-text-primary p-text-decoration-none p-text-sm hover:p-text-decoration-underline">
                        {{ getShortUrl(slotProps.data.anonymous_access_url) }}
                      </a>
                    </div>
                    <Button
                      icon="pi pi-copy"
                      class="p-button-text p-button-sm p-button-info"
                      @click="copyToClipboard(slotProps.data.anonymous_access_url)"
                      v-tooltip.top="'Копіювати посилання'"
                    />
                    <Button
                      icon="pi pi-external-link"
                      class="p-button-text p-button-sm p-button-success"
                      @click="openAnonymousTest(slotProps.data.anonymous_access_url)"
                      v-tooltip.top="'Відкрити тест'"
                    />
                  </div>
                  <span v-else-if="slotProps.data.is_anonymous" class="p-text-orange-500 p-text-sm p-font-italic">UUID не згенеровано</span>
                  <span v-else class="p-text-secondary p-text-sm">-</span>
                </template>
              </Column>
              <Column header="Дії">
                <template #body="slotProps">
                  <div class="p-flex p-gap-1">
                    <Button
                      icon="pi pi-eye"
                      class="p-button-text p-button-sm"
                      @click="viewSession(slotProps.data)"
                      v-tooltip.top="'Переглянути'"
                    />
                    <Button
                      icon="pi pi-pencil"
                      class="p-button-text p-button-sm"
                      @click="editSession(slotProps.data)"
                      v-tooltip.top="'Редагувати'"
                    />
                    <Button
                      icon="pi pi-calendar"
                      class="p-button-text p-button-sm"
                      @click="openUpdateDatesDialog(slotProps.data)"
                      v-tooltip.top="'Оновити дати'"
                    />
                    <Button
                      icon="pi pi-users"
                      class="p-button-text p-button-sm"
                      @click="openManageParticipantsDialog(slotProps.data)"
                      v-tooltip.top="'Учасники'"
                    />
                    <Button
                      v-if="slotProps.data.status === 'active'"
                      icon="pi pi-pause"
                      class="p-button-text p-button-sm p-button-warning"
                      @click="deactivateSessionAction(slotProps.data)"
                      v-tooltip.top="'Деактивувати'"
                    />
                    <Button
                      v-else
                      icon="pi pi-play"
                      class="p-button-text p-button-sm p-button-success"
                      @click="activateSessionAction(slotProps.data)"
                      v-tooltip.top="'Активувати'"
                    />
                    <Button
                      icon="pi pi-trash"
                      class="p-button-text p-button-sm p-button-danger"
                      @click="deleteSession(slotProps.data)"
                      v-tooltip.top="'Видалити'"
                    />
                  </div>
                </template>
              </Column>
            </DataTable>
          </div>
        </TabPanel>

        <TabPanel header="Типи тестів">
          <div class="p-mb-6">
            <div class="p-flex p-justify-between p-ai-center p-mb-6">
              <h2 class="p-m-0 p-text-2xl p-font-semibold p-text-color">Доступні типи тестів</h2>
              <Button
                label="Створити тип"
                icon="pi pi-plus"
                class="p-button-success"
                @click="showCreateTestTypeDialog = true"
              />
            </div>

            <DataTable :value="testTypes" :paginator="true" :rows="10" class="p-mb-6">
              <Column field="name" header="Назва"></Column>
              <Column field="category" header="Категорія">
                <template #body="slotProps">
                  <Tag :value="getCategoryLabel(slotProps.data.category)" severity="info" />
                </template>
              </Column>
              <Column field="questions.length" header="Питання"></Column>
              <Column field="is_active" header="Активний">
                <template #body="slotProps">
                  <i :class="slotProps.data.is_active ? 'pi pi-check text-green-500' : 'pi pi-times text-red-500'"></i>
                </template>
              </Column>
              <Column field="created_at" header="Створено">
                <template #body="slotProps">
                  {{ formatDate(slotProps.data.created_at) }}
                </template>
              </Column>
              <Column header="Дії">
                <template #body="slotProps">
                  <div class="p-flex p-gap-1">
                    <Button
                      icon="pi pi-eye"
                      class="p-button-text p-button-sm"
                      @click="viewTestType(slotProps.data)"
                      v-tooltip.top="'Переглянути'"
                    />
                    <Button
                      icon="pi pi-pencil"
                      class="p-button-text p-button-sm"
                      @click="editTestType(slotProps.data)"
                      v-tooltip.top="'Редагувати'"
                    />
                    <Button
                      icon="pi pi-list"
                      class="p-button-text p-button-sm"
                      @click="manageQuestions(slotProps.data)"
                      v-tooltip.top="'Питання'"
                    />
                    <Button
                      v-if="slotProps.data.is_active"
                      icon="pi pi-pause"
                      class="p-button-text p-button-sm p-button-warning"
                      @click="deactivateTestTypeAction(slotProps.data)"
                      v-tooltip.top="'Деактивувати'"
                    />
                    <Button
                      v-else
                      icon="pi pi-play"
                      class="p-button-text p-button-sm p-button-success"
                      @click="activateTestTypeAction(slotProps.data)"
                      v-tooltip.top="'Активувати'"
                    />
                  </div>
                </template>
              </Column>
            </DataTable>
          </div>
        </TabPanel>

        <TabPanel header="Користувачі">
          <div class="p-mb-6">
            <div class="p-flex p-justify-between p-ai-center p-mb-6">
              <h2 class="p-m-0 p-text-2xl p-font-semibold p-text-color">Користувачі системи</h2>
              <Button
                label="Додати користувача"
                icon="pi pi-plus"
                class="p-button-success"
                @click="showCreateUserDialog = true"
              />
            </div>

            <DataTable :value="users" :paginator="true" :rows="10" class="p-mb-6">
              <Column field="username" header="Логін"></Column>
              <Column field="first_name" header="Ім'я"></Column>
              <Column field="last_name" header="Прізвище"></Column>
              <Column field="email" header="Email"></Column>
              <Column field="role" header="Роль">
                <template #body="slotProps">
                  <Tag :value="getRoleLabel(slotProps.data.role)" :severity="slotProps.data.role === 'admin' ? 'danger' : 'info'" />
                </template>
              </Column>
              <Column field="department" header="Відділ"></Column>
              <Column field="is_active" header="Активний">
                <template #body="slotProps">
                  <i :class="slotProps.data.is_active ? 'pi pi-check text-green-500' : 'pi pi-times text-red-500'"></i>
                </template>
              </Column>
              <Column header="Дії">
                <template #body="slotProps">
                  <div class="p-flex p-gap-1">
                    <Button
                      icon="pi pi-eye"
                      class="p-button-text p-button-sm"
                      @click="viewUser(slotProps.data)"
                      v-tooltip.top="'Переглянути'"
                    />
                    <Button
                      icon="pi pi-pencil"
                      class="p-button-text p-button-sm"
                      @click="editUser(slotProps.data)"
                      v-tooltip.top="'Редагувати'"
                    />
                    <Button
                      icon="pi pi-key"
                      class="p-button-text p-button-sm"
                      @click="resetPassword(slotProps.data)"
                      v-tooltip.top="'Скинути пароль'"
                    />
                  </div>
                </template>
              </Column>
            </DataTable>
          </div>
        </TabPanel>

        <TabPanel header="Аналітика">
          <div class="p-mb-6">
            <div class="p-grid p-col-12 p-gap-6 p-mb-6">
              <div class="p-col-12 p-md-6 p-lg-6">
                <div class="p-bg-surface-card p-border-1 p-border-surface-border p-rounded-lg p-p-6">
                  <h3 class="p-m-0 p-mb-4 p-text-xl p-font-semibold p-text-color">Загальна статистика</h3>
                  <div class="p-flex p-flex-column p-gap-3">
                    <div class="p-flex p-justify-between p-ai-center">
                      <span class="p-text-secondary">Усього користувачів:</span>
                      <span class="p-text-primary p-font-semibold">{{ totalUsers }}</span>
                    </div>
                    <div class="p-flex p-justify-between p-ai-center">
                      <span class="p-text-secondary">Активних сесій:</span>
                      <span class="p-text-primary p-font-semibold">{{ activeSessionsCount }}</span>
                    </div>
                    <div class="p-flex p-justify-between p-ai-center">
                      <span class="p-text-secondary">Завершених тестів:</span>
                      <span class="p-text-primary p-font-semibold">{{ completedTestsCount }}</span>
                    </div>
                    <div class="p-flex p-justify-between p-ai-center">
                      <span class="p-text-secondary">Середній eNPS:</span>
                      <span class="p-text-primary p-font-semibold">{{ averageENPS }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="p-col-12 p-md-6 p-lg-6">
                <div class="p-bg-surface-card p-border-1 p-border-surface-border p-rounded-lg p-p-6">
                  <h3 class="p-m-0 p-mb-4 p-text-xl p-font-semibold p-text-color">Останні активності</h3>
                  <div class="p-flex p-flex-column p-gap-3">
                    <div v-for="activity in recentActivitiesList" :key="activity.id" class="p-flex p-ai-center p-gap-3">
                      <div class="p-text-primary">
                        <i class="pi pi-info-circle"></i>
                      </div>
                      <div class="p-flex-1">
                        <div class="p-text-color">{{ activity.action }}</div>
                        <div class="p-text-secondary p-text-sm">{{ formatTime(activity.created_at) }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="p-grid p-col-12 p-gap-6 p-mb-6">
              <div class="p-col-12 p-md-6 p-lg-6">
                <div class="p-bg-surface-card p-border-1 p-border-surface-border p-rounded-lg p-p-6">
                  <h3 class="p-m-0 p-mb-4 p-text-xl p-font-semibold p-text-color">Активність по місяцях</h3>
                  <div class="p-relative p-h-75">
                    <canvas ref="activityChart"></canvas>
                  </div>
                </div>
              </div>
              
              <div class="p-col-12 p-md-6 p-lg-6">
                <div class="p-bg-surface-card p-border-1 p-border-surface-border p-rounded-lg p-p-6">
                  <h3 class="p-m-0 p-mb-4 p-text-xl p-font-semibold p-text-color">Розподіл по відділах</h3>
                  <div class="p-relative p-h-75">
                    <canvas ref="departmentChart"></canvas>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </TabPanel>
      </TabView>
    </div>

    <!-- Dialogs -->
    <Dialog
      v-model:visible="showCreateSessionDialog"
      header="Створити нову сесію"
      :modal="true"
      class="p-dialog"
    >
      <div class="p-flex p-flex-column p-gap-4">
        <div class="p-flex p-flex-column p-gap-2">
          <label class="p-font-semibold p-text-color">Назва сесії:</label>
          <InputText v-model="newSession.name" placeholder="Введіть назву сесії" />
        </div>
        <div class="p-flex p-flex-column p-gap-2">
          <label class="p-font-semibold p-text-color">Тип тесту:</label>
          <Dropdown
            v-model="newSession.test_type"
            :options="activeTestTypes"
            optionLabel="name"
            placeholder="Виберіть тип тесту"
          />
        </div>
        <div class="p-flex p-flex-column p-gap-2">
          <label class="p-font-semibold p-text-color">Дата початку:</label>
          <Calendar v-model="newSession.start_date" showTime />
        </div>
        <div class="p-flex p-flex-column p-gap-2">
          <label class="p-font-semibold p-text-color">Дата завершення:</label>
          <Calendar v-model="newSession.end_date" showTime />
        </div>
        <div class="p-flex p-flex-column p-gap-2">
          <label class="p-font-semibold p-text-color">Опис:</label>
          <Textarea v-model="newSession.description" :rows="3" />
        </div>
        <div class="p-flex p-ai-center p-gap-2">
          <Checkbox v-model="newSession.is_anonymous" :binary="true" />
          <label class="p-font-semibold p-text-color">Анонімні відповіді</label>
        </div>
      </div>
      <template #footer>
        <Button label="Скасувати" @click="showCreateSessionDialog = false" class="p-button-text" />
                    <Button label="Створити" @click="createSessionAction" class="p-button-success" />
      </template>
    </Dialog>

    <!-- Update Dates Dialog -->
    <Dialog
      v-model:visible="showUpdateDatesDialog"
      header="Оновити дати сесії"
      :modal="true"
      class="p-dialog"
    >
      <div class="p-flex p-flex-column p-gap-4">
        <div class="p-flex p-flex-column p-gap-2">
          <label class="p-font-semibold p-text-color">Назва сесії:</label>
          <InputText v-model="editingSession.name" disabled />
        </div>
        <div class="p-flex p-flex-column p-gap-2">
          <label class="p-font-semibold p-text-color">Дата початку:</label>
          <Calendar v-model="editingSession.start_date" showTime />
        </div>
        <div class="p-flex p-flex-column p-gap-2">
          <label class="p-font-semibold p-text-color">Дата завершення:</label>
          <Calendar v-model="editingSession.end_date" showTime />
        </div>
      </div>
      <template #footer>
        <Button label="Скасувати" @click="showUpdateDatesDialog = false" class="p-button-text" />
        <Button label="Оновити" @click="updateSessionDatesAction" class="p-button-success" />
      </template>
    </Dialog>

    <!-- Manage Participants Dialog -->
    <Dialog
      v-model:visible="showManageParticipantsDialog"
      header="Управління учасниками"
      :modal="true"
      class="p-dialog"
      style="width: 80vw; max-width: 1000px;"
    >
      <div class="p-flex p-flex-column p-gap-4">
        <div class="p-mt-6">
          <div class="p-flex p-justify-between p-ai-center p-mb-6">
            <h3 class="p-m-0 p-text-xl p-font-semibold p-text-color">Поточні учасники ({{ currentParticipants.length }})</h3>
            <Button
              label="Додати учасників"
              icon="pi pi-plus"
              class="p-button-success"
              @click="openAddParticipantsDialog"
            />
          </div>
          
          <DataTable :value="currentParticipants" :paginator="true" :rows="10" class="p-mb-6">
            <Column field="user_name" header="Ім'я"></Column>
            <Column field="user_email" header="Email"></Column>
            <Column field="joined_date" header="Дата приєднання"></Column>
            <Column header="Дії">
              <template #body="slotProps">
                <Button
                  icon="pi pi-trash"
                  class="p-button-danger p-button-sm"
                  @click="removeParticipantAction(slotProps.data)"
                />
              </template>
            </Column>
          </DataTable>
        </div>
      </div>
      <template #footer>
        <Button label="Закрити" @click="showManageParticipantsDialog = false" class="p-button-text" />
      </template>
    </Dialog>

    <!-- Add Participants Dialog -->
    <Dialog
      v-model:visible="showAddParticipantsDialog"
      header="Додати учасників"
      :modal="true"
      class="p-dialog"
    >
      <div class="p-flex p-flex-column p-gap-4">
        <div v-if="!availableUsers || availableUsers.length === 0" class="p-flex p-ai-center p-text-red-500 p-bg-red-50 p-border-1 p-border-red-200 p-rounded p-p-3 p-mb-4">
          <i class="pi pi-exclamation-triangle p-mr-2"></i>
          <span>Немає доступних користувачів для додавання. Перевірте права доступу або спробуйте оновити сторінку.</span>
          <Button 
            label="Оновити список" 
            icon="pi pi-refresh" 
            class="p-button-text p-button-sm p-ml-4" 
            @click="refreshUsersData"
          />
        </div>
        <div v-else class="p-flex p-flex-column p-gap-2">
          <div class="p-flex p-justify-between p-ai-center p-mb-2">
            <label class="p-font-semibold p-text-color p-m-0">Виберіть користувачів:</label>
            <Button 
              label="Оновити" 
              icon="pi pi-refresh" 
              class="p-button-text p-button-sm" 
              @click="refreshUsersData"
            />
          </div>
          <MultiSelect
            v-model="selectedUsers"
            :options="availableUsers"
            optionLabel="displayName"
            placeholder="Виберіть користувачів"
            :filter="true"
          />
        </div>
      </div>
      <template #footer>
        <Button label="Скасувати" @click="showAddParticipantsDialog = false" class="p-button-text" />
        <Button 
          label="Додати" 
          @click="addParticipantsAction" 
          class="p-button-success"
          :disabled="!availableUsers || availableUsers.length === 0"
        />
      </template>
    </Dialog>

    <!-- Edit Session Name Dialog -->
    <Dialog
      v-model:visible="showEditSessionNameDialog"
      header="Змінити назву сесії"
      :modal="true"
      class="p-dialog"
    >
      <div class="p-flex p-flex-column p-gap-4">
        <div class="p-flex p-flex-column p-gap-2">
          <label class="p-font-semibold p-text-color">Назва сесії:</label>
          <InputText 
            v-model="editingSessionName" 
            placeholder="Введіть нову назву сесії"
            class="p-w-full"
          />
        </div>
      </div>
      <template #footer>
        <Button label="Скасувати" @click="showEditSessionNameDialog = false" class="p-button-text" />
        <Button label="Зберегти" @click="updateSessionNameAction" class="p-button-success" />
      </template>
    </Dialog>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { Chart, registerables } from 'chart.js'

Chart.register(...registerables)

export default {
  name: 'Admin',
  data() {
    return {
      showCreateSessionDialog: false,
      showCreateTestTypeDialog: false,
      showCreateUserDialog: false,
      showUpdateDatesDialog: false,
      showManageParticipantsDialog: false,
      showAddParticipantsDialog: false,
      showEditSessionNameDialog: false,
      newSession: {
        name: '',
        test_type: null,
        start_date: null,
        end_date: null,
        description: '',
        is_anonymous: false
      },
      editingSession: {
        id: null,
        name: '',
        start_date: null,
        end_date: null
      },
      editingSessionName: '',
      currentParticipants: [],
      selectedUsers: [],
      activityChart: null,
      departmentChart: null,
      recentActivitiesList: []
    }
  },
  computed: {
    ...mapState(['sessions', 'testTypes', 'users', 'results']),
    activeSessions() {
      return this.sessions
    },
    activeSessionsCount() {
      return this.activeSessions.length
    },
    totalUsers() {
      return this.users?.length || 0
    },
    completedTestsCount() {
      return this.results?.length || 0
    },
    averageENPS() {
      const enpsResults = this.results?.filter(r => r.enps_result) || []
      if (enpsResults.length === 0) return 'N/A'
      const total = enpsResults.reduce((sum, r) => sum + (r.enps_result.nps_score || 0), 0)
      return (total / enpsResults.length).toFixed(1)
    },
    availableUsers() {
      if (!this.editingSession.id) {
        return this.users
      }

      const currentUserIds = this.currentParticipants.map(p => p.id)
      const filteredUsers = this.users.filter(user => !currentUserIds.includes(user.id))
      return filteredUsers
    },
    activeTestTypes() {
      return this.testTypes?.filter(testType => testType.is_active) || []
    },
    formatUsersForDisplay() {
      return (users) => {
        if (!users) return []
        return users.map(user => ({
          ...user,
          displayName: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username
        }))
      }
    }
  },
  methods: {
    ...mapActions([
      'fetchSessions', 'fetchTestTypes', 'fetchUsers', 'fetchResults',
      'activateSession', 'deactivateSession', 'updateSessionDates',
      'addParticipants', 'removeParticipants', 'fetchSessionParticipants',
      'activateTestType', 'deactivateTestType', 'createSession',
      'fetchRecentActivities', 'deleteSession', 'updateSessionName'
    ]),
    async recentActivities() {
      try {
        await this.fetchRecentActivities()
        this.recentActivitiesList = this.$store.state.recentActivities
      } catch (error) {
        this.recentActivitiesList = []
      }
    },
    checkUserPermissions() {
      const currentUser = this.$store.state.currentUser
      if (!currentUser) {
        return false
      }

      if (currentUser.is_admin || currentUser.is_staff || currentUser.is_superuser) {
        return true
      }

      return false
    },
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('uk-UA')
    },
    formatTime(date) {
      return new Date(date).toLocaleString('uk-UA')
    },
    getStatusLabel(status) {
      const labels = {
        active: 'Активна',
        completed: 'Завершена',
        expired: 'Завершена',
        cancelled: 'Скасована'
      }
      return labels[status] || status
    },
    getStatusSeverity(status) {
      const severities = {
        active: 'success',
        completed: 'info',
        expired: 'warning',
        cancelled: 'danger'
      }
      return severities[status] || 'info'
    },
    getCategoryLabel(category) {
      const labels = {
        gallup_q12: 'Gallup Q12',
        enps: 'eNPS',
        custom: 'Кастомний'
      }
      return labels[category] || category
    },
    getRoleLabel(role) {
      const labels = {
        admin: 'Адміністратор',
        user: 'Користувач'
      }
      return labels[role] || role
    },
    viewSession(session) {
      // Navigate to session results page
      this.$router.push(`/session/${session.id}/results`)
    },
    editSession(session) {
      this.editingSession = {
        id: session.id,
        name: session.name,
        start_date: new Date(session.start_date),
        end_date: new Date(session.end_date)
      }
      this.editingSessionName = session.name
      this.showEditSessionNameDialog = true
    },
    async activateSessionAction(session) {
      try {
        await this.activateSession(session.id)
        this.$toast.add({
          severity: 'success',
          summary: 'Успішно',
          detail: `Сесія "${session.name}" активована`,
          life: 3000
        })
        await this.fetchSessions()
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: 'Помилка',
          detail: error.message || 'Не вдалося активувати сесію',
          life: 5000
        })
      }
    },
    async deactivateSessionAction(session) {
      try {
        await this.deactivateSession(session.id)
        this.$toast.add({
          severity: 'success',
          summary: 'Успішно',
          detail: `Сесія "${session.name}" деактивована`,
          life: 3000
        })
        await this.fetchSessions()
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: 'Помилка',
          detail: error.message || 'Не вдалося деактивувати сесію',
          life: 5000
        })
      }
    },
    openUpdateDatesDialog(session) {
      this.editingSession = {
        id: session.id,
        name: session.name,
        start_date: new Date(session.start_date),
        end_date: new Date(session.end_date)
      }
      this.showUpdateDatesDialog = true
    },
    async updateSessionDatesAction() {
      try {
        await this.updateSessionDates({
          sessionId: this.editingSession.id,
          startDate: this.editingSession.start_date,
          endDate: this.editingSession.end_date
        })
        this.$toast.add({
          severity: 'success',
          summary: 'Успішно',
          detail: 'Дати сесії оновлено',
          life: 3000
        })
        this.showUpdateDatesDialog = false
        await this.fetchSessions()
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: 'Помилка',
          detail: error.message || 'Не вдалося оновити дати',
          life: 5000
        })
      }
    },
    async updateSessionNameAction() {
      try {
        // Validation
        if (!this.editingSessionName.trim()) {
          this.$toast.add({
            severity: 'error',
            summary: 'Помилка',
            detail: 'Введіть назву сесії',
            life: 5000
          })
          return
        }

        await this.updateSessionName({
          sessionId: this.editingSession.id,
          name: this.editingSessionName.trim()
        })
        
        this.$toast.add({
          severity: 'success',
          summary: 'Успішно',
          detail: 'Назву сесії оновлено',
          life: 3000
        })
        this.showEditSessionNameDialog = false
        await this.fetchSessions()
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: 'Помилка',
          detail: error.message || 'Не вдалося оновити назву сесії',
          life: 5000
        })
      }
    },
    async openManageParticipantsDialog(session) {
      this.editingSession = session
      this.currentParticipants = await this.fetchSessionParticipants(session.id)
    },
    async addParticipantsAction() {
      if (!this.selectedUsers.length) {
        this.$toast.add({
          severity: 'warn',
          summary: 'Увага',
          detail: 'Виберіть користувачів для додавання',
          life: 3000
        })
        return
      }
      
      try {
        await this.addParticipants({
          sessionId: this.editingSession.id,
          userIds: this.selectedUsers.map(u => u.id)
        })
        this.$toast.add({
          severity: 'success',
          summary: 'Успішно',
          detail: 'Учасники додані',
          life: 3000
        })
        this.showAddParticipantsDialog = false
        this.selectedUsers = []
        this.currentParticipants = await this.fetchSessionParticipants(this.editingSession.id)
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: 'Помилка',
          detail: error.message || 'Не вдалося додати учасників',
          life: 5000
        })
      }
    },
    async removeParticipantAction(participant) {
      try {
        await this.removeParticipants({
          sessionId: this.editingSession.id,
          userIds: [participant.user]
        })
        this.$toast.add({
          severity: 'success',
          summary: 'Успішно',
          detail: 'Учасник видалений',
          life: 3000
        })
        this.currentParticipants = await this.fetchSessionParticipants(this.editingSession.id)
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: 'Помилка',
          detail: error.message || 'Не вдалося видалити учасника',
          life: 5000
        })
      }
    },
    async deleteSession(session) {
      this.$confirm.require({
        message: `Ви впевнені, що хочете видалити сесію "${session.name}"?`,
        header: 'Підтвердження видалення',
        icon: 'pi pi-exclamation-triangle',
        accept: async () => {
          try {
            await this.$store.dispatch('deleteSession', session.id)
            this.$toast.add({
              severity: 'success',
              summary: 'Успішно',
              detail: `Сесію "${session.name}" видалено`,
              life: 3000
            })
            await this.fetchSessions()
          } catch (error) {
            this.$toast.add({
              severity: 'error',
              summary: 'Помилка',
              detail: error.message || 'Не вдалося видалити сесію',
              life: 5000
            })
          }
        }
      })
    },
    viewTestType() {
      // Implementation for viewing test type
    },
    editTestType() {
      // Implementation for editing test type
    },
    manageQuestions() {
      // Implementation for managing questions
    },
    async activateTestTypeAction(testType) {
      try {
        await this.activateTestType(testType.id)
        this.$toast.add({
          severity: 'success',
          summary: 'Успішно',
          detail: `Тип тесту "${testType.name}" активовано`,
          life: 3000
        })
        await this.fetchTestTypes()
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: 'Помилка',
          detail: error.message || 'Не вдалося активувати тип тесту',
          life: 5000
        })
      }
    },
    async deactivateTestTypeAction(testType) {
      try {
        await this.deactivateTestType(testType.id)
        this.$toast.add({
          severity: 'success',
          summary: 'Успішно',
          detail: `Тип тесту "${testType.name}" деактивовано`,
          life: 3000
        })
        await this.fetchTestTypes()
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: 'Помилка',
          detail: error.message || 'Не вдалося деактивувати тип тесту',
          life: 5000
        })
      }
    },
    viewUser() {
      // Implementation for viewing user
    },
    editUser() {
      // Implementation for editing user
    },
    resetPassword() {
      // Implementation for resetting password
    },
    async createSessionAction() {
      try {
        // Валідація обов'язкових полів
        if (!this.newSession.name) {
          this.$toast.add({
            severity: 'error',
            summary: 'Помилка',
            detail: 'Введіть назву сесії',
            life: 5000
          })
          return
        }
        
        if (!this.newSession.test_type) {
          this.$toast.add({
            severity: 'error',
            summary: 'Помилка',
            detail: 'Виберіть тип тесту',
            life: 5000
          })
          return
        }
        
        if (!this.newSession.start_date) {
          this.$toast.add({
            severity: 'error',
            summary: 'Помилка',
            detail: 'Виберіть дату початку',
            life: 5000
          })
          return
        }
        
        if (!this.newSession.end_date) {
          this.$toast.add({
            severity: 'error',
            summary: 'Помилка',
            detail: 'Виберіть дату завершення',
            life: 5000
          })
          return
        }
        
        // Додаткова перевірка test_type
        if (!this.newSession.test_type.id) {
          this.$toast.add({
            severity: 'error',
            summary: 'Помилка',
            detail: 'Невірний тип тесту',
            life: 5000
          })
          return
        }
        
        // Перевірка активності тесту
        if (!this.newSession.test_type.is_active) {
          this.$toast.add({
            severity: 'error',
            summary: 'Помилка',
            detail: 'Не можна створити сесію з неактивним типом тесту',
            life: 5000
          })
          return
        }
        
        // Перетворюємо test_type об'єкт на test_type_id
        const sessionData = {
          ...this.newSession,
          test_type: this.newSession.test_type.id
        }
        
        await this.createSession(sessionData)
        this.$toast.add({
          severity: 'success',
          summary: 'Успішно',
          detail: 'Сесію створено',
          life: 3000
        })
        this.showCreateSessionDialog = false
        this.newSession = {
          name: '',
          test_type: null,
          start_date: null,
          end_date: null,
          description: '',
          is_anonymous: false
        }
        await this.fetchSessions()
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: 'Помилка',
          detail: error.message || 'Не вдалося створити сесію',
          life: 5000
        })
      }
    },
    initCharts() {
      this.initActivityChart()
      this.initDepartmentChart()
    },
    initActivityChart() {
      const ctx = this.$refs.activityChart
      if (ctx) {
        this.activityChart = new Chart(ctx, {
          type: 'line',
          data: {
            labels: ['Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень'],
            datasets: [{
              label: 'Активні користувачі',
              data: [65, 59, 80, 81, 56, 55],
              borderColor: '#3b82f6',
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              tension: 0.4
            }]
          },
          options: {
            responsive: true,
            plugins: {
              legend: {
                display: false
              }
            }
          }
        })
      }
    },
    initDepartmentChart() {
      const ctx = this.$refs.departmentChart
      if (ctx) {
        this.departmentChart = new Chart(ctx, {
          type: 'doughnut',
          data: {
            labels: ['IT', 'HR', 'Маркетинг', 'Продажі', 'Фінанси'],
            datasets: [{
              data: [30, 20, 15, 25, 10],
              backgroundColor: ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6']
            }]
          },
          options: {
            responsive: true,
            plugins: {
              legend: {
                position: 'bottom'
              }
            }
          }
        })
      }
    },
    openAddParticipantsDialog() {
      if (!this.editingSession.id) return
      this.showAddParticipantsDialog = true
    },
    async refreshUsersData() {
      try {
        await this.fetchUsers()
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: 'Помилка',
          detail: 'Не вдалося оновити список користувачів',
          life: 5000
        })
      }
    },
    // Анонімні сесії методи
    hasAnonymousSessions() {
      return this.sessions.some(session => session.is_anonymous)
    },
    getShortUrl(url) {
      if (!url) return ''
      try {
        const urlObj = new URL(url)
        return `${urlObj.hostname}${urlObj.pathname}`
      } catch {
        return url
      }
    },
    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text)
        this.$toast.add({
          severity: 'success',
          summary: 'Скопійовано',
          detail: 'Посилання скопійовано в буфер обміну',
          life: 3000
        })
      } catch (err) {
        this.$toast.add({
          severity: 'error',
          summary: 'Помилка',
          detail: 'Не вдалося скопіювати посилання',
          life: 5000
        })
      }
    },
    openAnonymousTest(url) {
      window.open(url, '_blank')
    },
    getAnonymousCompletionRate(session) {
      if (!session.is_anonymous || !session.anonymous_responses_count) return 0
      
      const totalQuestions = session.test_type?.questions?.length || 0
      if (totalQuestions === 0) return 0
      
      // Приблизна оцінка на основі кількості відповідей
      const estimatedCompletions = Math.floor(session.anonymous_responses_count / totalQuestions)
      const maxPossible = session.max_anonymous_responses || 1000
      
      return Math.min(100, Math.round((estimatedCompletions / maxPossible) * 100))
    }
  },
  async mounted() {
    // Check user permissions first
    const hasPermissions = this.checkUserPermissions()
    if (!hasPermissions) {
      return
    }
    
    await Promise.all([
      this.fetchSessions(),
      this.fetchTestTypes(),
      this.fetchUsers(),
      this.fetchResults(),
      this.recentActivities()
    ])
    
    this.$nextTick(() => {
      this.initCharts()
    })
  },
  beforeUnmount() {
    if (this.activityChart) {
      this.activityChart.destroy()
    }
    if (this.departmentChart) {
      this.departmentChart.destroy()
    }
  }
}
</script>

<style scoped>
/* PrimeVue utility classes now handle all styling */
/* No custom CSS needed - all styling is done through PrimeVue classes */

/* Custom hover effect for anonymous links */
.hover\:p-text-decoration-underline:hover {
  text-decoration: underline;
}

/* Responsive adjustments for mobile */
@media (max-width: 768px) {
  .p-p-6 {
    padding: 1rem;
  }
  
  .p-mb-6 {
    margin-bottom: 1rem;
  }
  
  .p-gap-6 {
    gap: 1rem;
  }
}
</style> 